import { useEffect, useState, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search,
  X,
  BadgeCheck,
  Sparkles,
  HeartHandshake,
  Loader2,
  Link2Off,
  ArrowLeftRight,
  Users2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag } from "@/components/shared";
import { ConfirmDialog } from "@/components/dialogs";

function UserPicker({ label, slot, selected, onSelect, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (selected) return;
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.searchUsers(q.trim());
        setResults(Array.isArray(res) ? res : res?.users || []);
        setOpen(true);
      } catch (e) {
        toast.error(e.message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, selected]);

  return (
    <div className="rounded-2xl bg-card ring-1 ring-border/70 p-5" data-testid={`picker-${slot}`}>
      <div className="text-xs uppercase tracking-wide text-primary font-semibold mb-3">{label}</div>

      {selected ? (
        <div className="flex items-center gap-3" data-testid={`selected-${slot}`}>
          <Avatar src={selected.photo} name={selected.name} size={48} />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate flex items-center gap-1.5">
              {selected.name || selected.user_id}
              {selected.verified && <BadgeCheck className="h-4 w-4 text-emerald-400" />}
              {selected.is_demo && <Tag tone="amber"><Sparkles className="h-3 w-3" />Demo</Tag>}
            </div>
            <div className="text-xs text-muted-foreground truncate">{selected.email || selected.user_id}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClear} data-testid={`clear-${slot}`}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email or user_id…"
            className="pl-9"
            data-testid={`search-${slot}`}
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}

          {open && (
            <div className="absolute z-20 mt-2 w-full rounded-xl bg-popover ring-1 ring-border shadow-xl max-h-72 overflow-y-auto" data-testid={`results-${slot}`}>
              {results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-muted-foreground">No matches</div>
              ) : (
                results.map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => {
                      onSelect(u);
                      setQ("");
                      setResults([]);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/70 text-left transition-colors"
                    data-testid={`result-${u.user_id}`}
                  >
                    <Avatar src={u.photo} name={u.name} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {u.name || u.user_id}
                        {u.verified && <BadgeCheck className="h-3.5 w-3.5 text-emerald-400" />}
                        {u.is_demo && <Tag tone="amber">Demo</Tag>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{u.email || u.user_id}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Matchmaker() {
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [creating, setCreating] = useState(false);
  const [matches, setMatches] = useState([]); // session-created matches
  const [unmatchTarget, setUnmatchTarget] = useState(null);

  const swap = () => {
    setA(b);
    setB(a);
  };

  const create = async () => {
    if (!a || !b) return;
    if (a.user_id === b.user_id) {
      toast.error("Pick two different users");
      return;
    }
    setCreating(true);
    try {
      const res = await api.createMatch(a.user_id, b.user_id);
      toast.success(res?.created === false ? "Already matched (no duplicate created)" : "Match created");
      setMatches((prev) => [
        { match_id: res.match_id, created: res.created, users: res.users || [a, b], at: Date.now() },
        ...prev.filter((m) => m.match_id !== res.match_id),
      ]);
      setA(null);
      setB(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const doUnmatch = async () => {
    try {
      await api.unmatch(unmatchTarget.match_id);
      toast.success("Unmatched & cleaned up");
      setMatches((prev) => prev.filter((m) => m.match_id !== unmatchTarget.match_id));
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Matchmaker"
        subtitle="Manually match two users for testing or support. Creates a real match and mutual likes."
      />

      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-center">
        <UserPicker label="User A" slot="a" selected={a} onSelect={setA} onClear={() => setA(null)} />
        <div className="flex md:flex-col justify-center">
          <Button variant="outline" size="icon" onClick={swap} disabled={!a && !b} className="rounded-full" data-testid="swap-btn" title="Swap">
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
        </div>
        <UserPicker label="User B" slot="b" selected={b} onSelect={setB} onClear={() => setB(null)} />
      </div>

      <div className="flex justify-center mt-5">
        <Button size="lg" onClick={create} disabled={!a || !b || creating} data-testid="create-match-btn">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <HeartHandshake className="h-4 w-4" />}
          Create match
        </Button>
      </div>

      {matches.length > 0 && (
        <div className="mt-10">
          <h3 className="font-display text-lg mb-3">Matches created this session</h3>
          <div className="space-y-3" data-testid="session-matches">
            {matches.map((m) => {
              const u1 = m.users?.[0] || {};
              const u2 = m.users?.[1] || {};
              return (
                <div key={m.match_id} className="rounded-2xl bg-card ring-1 ring-border/70 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`match-${m.match_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex -space-x-3">
                      <Avatar src={u1.photo} name={u1.name} size={40} className="ring-2 ring-card" />
                      <Avatar src={u2.photo} name={u2.name} size={40} className="ring-2 ring-card" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {(u1.name || u1.user_id || "User A")} <span className="text-muted-foreground">×</span> {(u2.name || u2.user_id || "User B")}
                      </div>
                      <div className="text-xs font-mono text-muted-foreground truncate">{m.match_id}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag tone={m.created ? "green" : "muted"}>{m.created ? "Newly created" : "Already matched"}</Tag>
                    <Button variant="destructive" size="sm" onClick={() => setUnmatchTarget(m)} data-testid={`unmatch-${m.match_id}`}>
                      <Link2Off className="h-4 w-4" /> Unmatch
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {matches.length === 0 && (
        <div className="mt-10 flex items-center gap-2 text-sm text-muted-foreground justify-center">
          <Users2 className="h-4 w-4" /> Pick two users above to create a match. Matches you create appear here so you can unmatch them.
        </div>
      )}

      <ConfirmDialog
        open={!!unmatchTarget}
        onOpenChange={(v) => !v && setUnmatchTarget(null)}
        title="Unmatch these users?"
        description="This removes the match, its chat messages, any watch-party room, and the swipe records. This cannot be undone."
        confirmText="Unmatch"
        destructive
        onConfirm={doUnmatch}
      />
    </div>
  );
}
